<template>
    <section class="section coach-passes">
        <div class="container">
            <div class="columns">
                <div class="column">
                    <h1 class="title">
                        {{ translate("page.admin_coach_passes.title") }}
                    </h1>
                    <p class="mb-3">
                        {{ translate("page.admin_coach_passes.subtitle") }}
                    </p>
                    <div class="buttons">
                        <a class="button" :href="urls.eventAdmin().coachPassesCsv(eventId, settingsQuery)">
                            <span class="icon">
                                <i class="fa fa-download"></i>
                            </span>
                            <span>{{ translate("common.download_in_csv") }}</span>
                        </a>
                        <a
                            v-if="!loading && hasIncludedMembers"
                            class="button"
                            :href="urls.eventAdmin().coachPassesPdf(eventId, settingsQuery)"
                        >
                            <span class="icon">
                                <i class="fas fa-file-pdf"></i>
                            </span>
                            <span>{{ translate("common.download_in_pdf") }}</span>
                        </a>
                    </div>
                </div>
            </div>

            <div v-if="loading" class="columns is-centered">
                <LoadingSpinner2 />
            </div>

            <template v-else>
                <CoachPassSettingsOverview class="mb-5" :settings="appliedSettings" @change-options="openSettings" />

                <div class="field mb-5">
                    <div class="control has-icons-left">
                        <input
                            class="input"
                            type="text"
                            v-model="filterText"
                            :placeholder="translate('page.admin_coach_passes.filter_placeholder')"
                        />
                        <span class="icon is-left"><i class="fas fa-search"></i></span>
                    </div>
                </div>

                <p v-if="listing.members.length > 0" class="mb-3" data-testid="coach-passes-count">
                    {{ passCountText }}
                </p>

                <div v-if="listing.members.length === 0" class="notification">
                    {{ translate("page.admin_coach_passes.empty_state") }}
                </div>

                <div v-else class="is-relative">
                    <div v-if="reloading" class="is-overlay is-flex is-justify-content-center is-align-items-center reload-overlay">
                        <LoadingSpinner2 />
                    </div>
                    <div v-if="filteredMembers.length > 0" class="table-container is-hidden-touch">
                        <table class="table is-fullwidth is-striped is-hoverable">
                            <thead>
                                <tr>
                                    <th>
                                        {{ translate("page.admin_coach_passes.column_academy") }}
                                    </th>
                                    <th>
                                        {{ translate("page.admin_coach_passes.column_name") }}
                                    </th>
                                    <th>
                                        {{ translate("page.admin_coach_passes.column_role") }}
                                    </th>
                                    <th>
                                        {{ translate("page.admin_coach_passes.column_email") }}
                                    </th>
                                    <th>
                                        {{ translate("page.admin_coach_passes.column_account_type") }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(entry, index) in filteredMembers" :key="index" :class="{ 'is-not-included': !entry.included }">
                                    <td>{{ entry.academyName }}</td>
                                    <td>
                                        <span v-if="entry.hasCoach" class="clarity-mask">{{ entry.name }}</span>
                                        <span v-else class="has-text-warning">{{ blankLabel(entry) }}</span>
                                        <span v-if="!entry.included" class="tag is-warning is-light ml-2">
                                            {{ translate("page.admin_coach_passes.not_included") }}
                                        </span>
                                    </td>
                                    <td>
                                        {{ entry.role ? translateAcademyRole(entry.role) : "—" }}
                                    </td>
                                    <td class="clarity-mask">{{ entry.email ?? "—" }}</td>
                                    <td>
                                        <span class="tag is-white" v-if="entry.hasCoach && entry.isShadow">
                                            {{ translate("page.admin_coach_passes.no_account") }}
                                        </span>
                                        <span class="tag is-info" v-else-if="entry.hasCoach">
                                            {{ translate("page.admin_coach_passes.has_account") }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="is-hidden-desktop">
                        <div
                            class="box has-no-glow-effect is-condensed"
                            :class="{ 'is-not-included': !entry.included }"
                            v-for="(entry, index) in filteredMembers"
                            :key="index"
                        >
                            <p class="has-text-weight-bold clarity-mask" v-if="entry.hasCoach">
                                {{ entry.name }}
                            </p>
                            <p class="has-text-weight-bold has-text-warning" v-else>
                                {{ blankLabel(entry) }}
                            </p>
                            <p class="mt-1" v-if="!entry.included">
                                <span class="tag is-warning is-light">{{ translate("page.admin_coach_passes.not_included") }}</span>
                            </p>
                            <p class="is-size-7 mt-1">
                                <span class="has-text-grey-light">{{ translate("page.admin_coach_passes.column_academy") }}: </span>
                                <span class="has-text-white">{{ entry.academyName }}</span>
                            </p>
                            <p class="is-size-7 mt-1" v-if="entry.hasCoach">
                                <span class="has-text-grey-light">{{ translate("page.admin_coach_passes.column_role") }}: </span>
                                <span class="has-text-white">{{ translateAcademyRole(entry.role!) }}</span>
                            </p>
                            <p class="is-size-7 mt-1" v-if="entry.hasCoach">
                                <span class="has-text-grey-light">{{ translate("page.admin_coach_passes.column_email") }}: </span>
                                <span class="has-text-white clarity-mask">{{ entry.email }}</span>
                            </p>
                            <p class="is-size-7 mt-1" v-if="entry.hasCoach">
                                <span class="tag is-white" v-if="entry.isShadow">
                                    {{ translate("page.admin_coach_passes.no_account") }}
                                </span>
                                <span class="tag is-info" v-else>
                                    {{ translate("page.admin_coach_passes.has_account") }}
                                </span>
                            </p>
                        </div>
                    </div>

                    <div v-if="filteredMembers.length === 0" class="notification">
                        {{ translate("page.admin_coach_passes.no_filter_matches") }}
                    </div>
                </div>
            </template>
        </div>
    </section>

    <CoachPassSettingsModal ref="settingsModal" @save="applySettings" />
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from "vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import CoachPassSettingsOverview from "./CoachPassSettingsOverview.vue";
    import CoachPassSettingsModal from "./CoachPassSettingsModal.vue";
    import coachPassesApi, { CoachPassEntry, CoachPassListing, CoachPassSettings, coachPassSettingsQuery } from "../Api/coachPasses";
    import { loadCoachPassSettings, saveCoachPassSettings } from "./coachPassSettingsStorage";
    import { translateAcademyRole } from "../Api/academies";
    import objectMatcher from "../Common/objectMatcher";
    import urls from "../Urls/urls";

    const props = defineProps<{
        eventId: number;
    }>();

    const initialSettings = loadCoachPassSettings(props.eventId);

    const loading = ref(true);
    const reloading = ref(false);
    const filterText = ref("");
    const listing = ref<CoachPassListing>({ members: [] });
    // Settings the shown listing was loaded with; the CSV and PDF links use the same ones.
    const appliedSettings = ref<CoachPassSettings>(initialSettings);
    let lastRequestId = 0;

    const settingsQuery = computed(() => coachPassSettingsQuery(appliedSettings.value));
    const hasIncludedMembers = computed(() => listing.value.members.some(entry => entry.included));
    const includedCount = computed(() => listing.value.members.filter(entry => entry.included).length);
    const notIncludedCount = computed(() => listing.value.members.length - includedCount.value);
    const passCountText = computed(() =>
        notIncludedCount.value > 0
            ? translate("page.admin_coach_passes.pass_count_with_cut", false, [includedCount.value, notIncludedCount.value])
            : translate("page.admin_coach_passes.pass_count", false, [includedCount.value]),
    );

    const filteredMembers = computed(() =>
        listing.value.members.filter(entry => objectMatcher.matchString(filterText.value, entry.academyName + " " + entry.name)),
    );

    function blankLabel(entry: CoachPassEntry): string {
        return translate(entry.isBlankPass ? "page.admin_coach_passes.blank_pass" : "page.admin_coach_passes.no_coach_assigned");
    }

    function loadListing(initial: boolean) {
        const requestId = ++lastRequestId;
        if (initial) {
            loading.value = true;
        } else {
            reloading.value = true;
        }
        coachPassesApi
            .getListing(props.eventId, appliedSettings.value)
            .then(result => {
                if (requestId === lastRequestId) {
                    listing.value = result;
                }
            })
            .catch(handleError)
            .finally(() => {
                if (requestId === lastRequestId) {
                    loading.value = false;
                    reloading.value = false;
                }
            });
    }

    const settingsModal = ref<InstanceType<typeof CoachPassSettingsModal> | null>(null);

    function openSettings() {
        settingsModal.value?.open(appliedSettings.value);
    }

    function applySettings(next: CoachPassSettings) {
        if (JSON.stringify(next) === JSON.stringify(appliedSettings.value)) {
            return;
        }
        appliedSettings.value = next;
        saveCoachPassSettings(props.eventId, next);
        loadListing(false);
    }

    onMounted(() => loadListing(true));
</script>

<style scoped>
    .reload-overlay {
        background: rgba(26, 38, 49, 0.6);
        z-index: 2;
    }

    .is-not-included {
        opacity: 0.45;
    }
</style>
